import React, { useState, createContext, useContext, ReactNode } from 'react'

import { HandlerFn } from '@at/design-system-elements'
import loadable from '@loadable/component'
import { SignInEntityContextProvider, SignInTrigger, SignInMode } from '@at/connected-signin'

const LoadableModalSignInComponent = loadable(
    () => import('@at/connected-signin').then((mod) => mod.ModalSignInComponent),
    {
        fallback: <div>Loading...</div>,
    }
)

interface HandleToggleModalLoginProps {
    showing: boolean
    signInMode?: SignInMode
    afterSignInCallback?: HandlerFn
    modalCloseCallback?: HandlerFn
}

type ModalLoginContextType = {
    isShowingModalLogin: boolean
    handleToggleModalLogin: (modalProps: HandleToggleModalLoginProps) => void
}

const ModalLoginContext = createContext<ModalLoginContextType>({
    isShowingModalLogin: false,
    handleToggleModalLogin: () => {},
})

ModalLoginContext.displayName = 'ModalLoginContext'

interface ModalLoginContextProviderProps {
    trigger: SignInTrigger
    children: ReactNode
}

export const ModalLoginContextProvider: React.FC<ModalLoginContextProviderProps> = ({ trigger, children }) => {
    const [modalLogin, setModalLogin] = useState<HandleToggleModalLoginProps>({
        showing: false,
        signInMode: SignInMode.SavedAdvert,
        afterSignInCallback: () => {},
        modalCloseCallback: () => {},
    })

    const handleToggleModalLogin = (modalProps: HandleToggleModalLoginProps) => {
        const currentModalLogin = { ...modalLogin, ...modalProps }
        setModalLogin(currentModalLogin)
    }

    const modalLoginValues = {
        handleToggleModalLogin,
        isShowingModalLogin: modalLogin.showing,
    }

    return (
        <SignInEntityContextProvider trigger={trigger}>
            <ModalLoginContext.Provider value={modalLoginValues}>
                {children}
                {modalLogin.showing && (
                    <LoadableModalSignInComponent
                        showing={true}
                        setShowing={() =>
                            setModalLogin((prevProps) => {
                                return { ...prevProps, showing: false }
                            })
                        }
                        afterSignInCallback={modalLogin.afterSignInCallback}
                        modalCloseCallback={modalLogin.modalCloseCallback}
                        signInMode={modalLogin.signInMode}
                    />
                )}
            </ModalLoginContext.Provider>
        </SignInEntityContextProvider>
    )
}

export const useModalLoginContext = () => {
    return useContext(ModalLoginContext)
}
